import { React, useEffect, useState, useRef } from "react";

import { Link } from "react-router-dom";

import axios from "axios";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";

import { DateOnlyFormat, currencyAmount } from "../../helpers/TemplateHelper";

import JONDdDialog from "../components/joborderno-deliverydate-component/JONDdDialog";

const Quotations = () => {
  const toast = useRef(null);

  const [orders, setOrders] = useState(null);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [height, setHeight] = useState(0);

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderTotal, setSelectedOrderTotal] = useState(0);
  const [showJONDdDialog, setShowJONDdDialog] = useState(false);

  const dt = useRef(null);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      const vh = window.innerHeight;
      const specificAmount = 320;
      setHeight(vh - specificAmount);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const getData = async () => {
    axios
      .get("/api/orders/quotations")
      .then((res) => {
        setOrders(res.data);
      })
      .catch((error) => {
        console.log(error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while processing your request.",
          life: 3000,
        });
      });
  };

  const confirmReject = (orderId) => {
    axios
      .post(`/api/orders/reject/${orderId}`)
      .then(() => {
        toast.current.show({
          severity: "success",
          summary: "Order Rejected",
          detail: "Order has been successfully rejected.",
          life: 3000,
        });

        setOrders((prevOrders) =>
          prevOrders.filter((order) => order.id !== orderId)
        );
        getData();
      })
      .catch((error) => {
        console.log(error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while rejecting the order.",
          life: 3000,
        });
      });
  };

  const handleConfirmReject = (orderId) => {
    confirmDialog({
      message: "Are you sure you want to reject this order?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => confirmReject(orderId),
    });
  };

  const calculateTotalAmount = (order) => {
    const {
      orderDetails: { subTotal, transportationCost, fixingCost, discountAmount },
    } = order;

    const discount = discountAmount || 0;

    return subTotal + transportationCost + fixingCost - discount;
  };

  const viewButtonTemplate = (rowData) => {
    return (
      <div>
        <Link to={`/pages/order/${rowData.id}`}>
          <Button
            icon="pi pi-external-link"
            className="p-button-rounded p-button-info mr-2"
            tooltip="View"
            tooltipOptions={{ event: "both" }}
          ></Button>
        </Link>
        <Button
          icon="pi pi-check"
          className="p-button-rounded p-button-success mr-2"
          tooltip="Accept"
          tooltipOptions={{ event: "both" }}
          onClick={() => {
            setSelectedOrderId(rowData.id);
            setSelectedOrderTotal(calculateTotalAmount(rowData));
            setShowJONDdDialog(true);
          }}
        ></Button>
        <Button
          icon="pi pi-times"
          className="p-button-rounded p-button-danger"
          tooltip="Reject"
          tooltipOptions={{ event: "both" }}
          onClick={() => handleConfirmReject(rowData.id)}
        ></Button>
      </div>
    );
  };

  const cols = [
    {
      header: "",
      body: viewButtonTemplate,
    },
    {
      field: "id",
      header: "ID",
      body: (row) => row.id.toString().padStart(8, "0"),
    },
    {
      field: "customerDetails.fullName",
      header: "Customer Name",
    },
    {
      field: "customerDetails.telephone",
      header: "Telephone",
    },
    {
      field: "created",
      header: "Created",
      body: (row) => <DateOnlyFormat date={row.created} />,
    },
    {
      className: "text-right",
      header: "Sets",
      field: "orderDetails.totalQty",
    },
    {
      className: "text-right",
      header: "Total Pleats",
      field: "orderDetails.totalPleats",
    },
    {
      className: "text-right",
      header: "Discount",
      headerStyle: { textAlign: "right" },
      body: (rowData) => `${currencyAmount(rowData.orderDetails?.discountAmount || 0)}`,
    },
    {
      className: "text-right",
      header: "Amount",
      headerStyle: { textAlign: "right" },
      body: (rowData) => `${currencyAmount(calculateTotalAmount(rowData))}`,
    },
  ];

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  return (
    <div className="card flex flex-column gap-4">
      <Toast ref={toast} position="bottom-right" />
      <JONDdDialog
        visible={showJONDdDialog}
        orderId={selectedOrderId}
        orderTotal={selectedOrderTotal}
        toastrRef={toast}
        onCancel={() => setShowJONDdDialog(false)}
        onOrderAccepted={(acceptedOrder) => {
          setShowJONDdDialog(false);
          setOrders((prevOrders) =>
            prevOrders.filter((order) => order.id !== acceptedOrder.id)
          );
        }}
      />
      ;
      <div className="flex justify-content-between">
        <div>
          <h4 className="text-primary mb-1">Quotations</h4>
          <label className="text-secondary">View all unsorted Quotations</label>
        </div>
        <div className="flex gap-2 align-items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              className="p-inputtext-sm"
              type="search"
              onInput={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search"
            />
          </span>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            severity="help"
            onClick={getData}
          />
          <Button
            label="Export"
            icon="pi pi-file-excel"
            severity="success"
            onClick={() => exportCSV(false)}
          />
        </div>
      </div>
      <div>
        <DataTable
          ref={dt}
          value={orders}
          dataKey="id"
          paginator
          rows={25}
          stripedRows
          rowsPerPageOptions={[25, 50, 100]}
          globalFilter={globalFilter}
          scrollable
          scrollHeight={height}
        >
          {cols.map((col, index) => (
            <Column key={index} {...col} sortable></Column>
          ))}
        </DataTable>
      </div>
    </div>
  );
};

export default Quotations;
